import React, { useState, MouseEvent } from "react";
import { FiChevronDown } from "react-icons/fi";
import Button from "./ui/Button";
import useClickOutside from "@/hooks/useClickOutSide";

type ButtonWithActionsProps = {
  dropdownClassName?: string;
  listClassName?: string;
  btnClassName?: string;
  text?: string;
  childOptions: { name: string; label: string; onClick: () => void; className?: string }[];
};

const ButtonWithActions: React.FC<ButtonWithActionsProps> = ({ childOptions, dropdownClassName = "", listClassName = "", btnClassName = "" }) => {
  const [showActions, setShowActions] = useState(false);
  const actionsRef = useClickOutside(() => setShowActions(false));

  const handleOnClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setShowActions((pre) => !pre);
  };

  return (
    <div className="flex items-center h-8 relative z-20" ref={actionsRef}>
      <div className=" flex items-center h-full">
        <Button variant="primary" size="small" onClick={handleOnClick} className={`rounded-r-none !px-3 ${btnClassName}`}>
          Actions
        </Button>
        <button
          className={`px-2 bg-text-primary text-white hover:bg-brand-secondary  h-full max-h-[26px] md:max-h-8 rounded-r-lg border-l ${btnClassName}`}
          onClick={handleOnClick}
        >
          <FiChevronDown size={16} className={showActions ? " rotate-180" : ""} />
        </button>
      </div>
      {/* Dropdown Menu */}
      {showActions && (
        <div className={`absolute p-1 w-40 bg-white border border-gray-200 shadow-lg rounded-md z-40 ${dropdownClassName}`}>
          {childOptions.map((option) => (
            <div
              key={option.name}
              className={`px-4 py-2 hover:bg-primaryOpacity-10 hover:text-text-primary cursor-pointer text-sm text-gray-700 ${listClassName}`}
              onClick={() => {
                setShowActions(false);
                option.onClick();
              }}
            >
              <span className="whitespace-nowrap"> {option.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ActionsDropdown: React.FC<ButtonWithActionsProps> = ({
  childOptions,
  dropdownClassName = "",
  listClassName = "",
  btnClassName = "",
  text = "Actions",
}) => {
  const [showActions, setShowActions] = useState(false);
  const actionsRef = useClickOutside(() => setShowActions(false));

  const toggleDropdown = () => {
    setShowActions(!showActions);
  };

  return (
    <div className="relative" ref={actionsRef}>
      <div className="flex items-center justify-center h-full">
        <button
          className={`flex items-center gap-1 rounded-md text-13 !leading-normal bg-purple-50 text-purple-600 hover:bg-purple-100 transition-colors hover:cursor-pointer ${btnClassName}`}
          onClick={toggleDropdown}
        >
          <span className="py-2 pr-2 pl-4">{text}</span>
          <div className="p-1.5 border-l-[1px] h-full hover:cursor-pointer border-white hover:bg-purple-100 transition-colors" onClick={toggleDropdown}>
            <FiChevronDown size={16} className={`transition-transform duration-200 ${showActions ? "rotate-180" : ""}`} />
          </div>
        </button>
      </div>

      {showActions && (
        <div
          className={`absolute top-full right-0 p-1 mt-1 w-40 bg-white border border-gray-200 shadow-lg rounded-md z-50 overflow-hidden ${dropdownClassName}`}
        >
          {childOptions.map((option) => (
            <div
              key={option.name}
              className={`px-4 py-2 hover:bg-primaryOpacity-10 hover:text-text-primary cursor-pointer text-13 !leading-normal text-gray-700 transition-colors ${listClassName} ${option?.className}`}
              onClick={() => {
                setShowActions(false);
                option.onClick();
              }}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export { ActionsDropdown, ButtonWithActions };
